import {_} from 'lodash';
import * as ko from "knockout";
import {ICompanyLogin, IMessageData} from "../../../index";
import {global, lm} from "../../../appContext";
import {ErrorTracker} from "../../../error.tracker";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {createErrorMessage} from "../../../util/misc";
import {IFormData} from "./index";
import {getHash} from "../../../constants/RouteDefinitions";
import {NO_DATA_MESSAGE, REQUEST_MESSAGE} from "../../../constants/Language";
import {IComboboxV2Options} from "../../combobox/v2";
import {createBaseTextMatch} from "../../combobox/v2-sources";
import detailsHtml from './details.html?raw';

export let template = detailsHtml;

type TMethod = "sms" | "idporten" | "teliafi" | "bankidse";

interface IMethod {
    value: TMethod;
    name: string;
    component: string;
}

const Methods: Record<TMethod, IMethod> = {
    idporten: {
        value: "idporten",
        name: "ID-porten",
        component: "component/password/forgot/method-idporten",
    },
    sms: {
        value: "sms",
        name: "SMS",
        component: "component/password/forgot/method-sms",
    },
    teliafi: {
        value: "teliafi",
        name: "((Telia)) ((Finland))",
        component: "component/password/forgot/method-teliafi",
    },
    bankidse: {
        value: "bankidse",
        name: "BankID SE",
        component: "component/password/forgot/method-bankidse",
    },
};

export class viewModel {
    private organizations!: ICompanyLogin[];
    private readonly isReady: KnockoutObservable<boolean> = ko.observable(false);
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly reqGetCompanyLogin = global.getPromiseRequest("getcompanylogin");
    readonly selectedMethod: KnockoutObservable<IMethod | null> = ko.observable(null);
    readonly selectedOrg: KnockoutObservable<ICompanyLogin | null> = ko.observable(null);
    readonly organizationsArr = ko.observableArray<ICompanyLogin>([]);
    readonly methodFormVisible = ko.observable(false);
    readonly forgotOrgComboboxOptions: IComboboxV2Options<ICompanyLogin>;
    readonly footerYear = ko.pureComputed((): number => new Date().getFullYear());
    readonly loginHref = ko.pureComputed((): string => getHash("formlogin") ?? "#/formlogin");
    readonly et: ErrorTracker = global.errorAreas.get("forgot.password");
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    readonly formData: IFormData = {
        et: this.et,
        isReady: this.isReady,
        messageData: this.messageData as unknown as KnockoutObservable<IMessageData>,
        mobile: ko.observable(""),
        selectedOrg: this.selectedOrg as unknown as KnockoutObservable<ICompanyLogin>,
        ssn: ko.observable(""),
        username: ko.observable(""),
    };

    readonly availableMethods = ko.pureComputed((): IMethod[] => {
        const ret: IMethod[] = [];
        if (this.selectedOrg()?.forgotpassordidporten) {
            ret.push(Methods.idporten);
        }
        if (this.selectedOrg()?.forgotpassordsms) {
            ret.push(Methods.sms);
        }
        if (this.selectedOrg()?.forgotpassordteliafi) {
            ret.push(Methods.teliafi);
        }
        if (this.selectedOrg()?.forgotpassordbankidse) {
            ret.push(Methods.bankidse);
        }
        return ret;
    });

    readonly canContinueForgot = ko.pureComputed((): boolean => {
        if (!this.isReady() || this.methodFormVisible()) {
            return false;
        }
        if (!this.selectedOrg()) {
            return false;
        }
        const methods = this.availableMethods();
        if (methods.length === 0) {
            return false;
        }
        if (methods.length === 1) {
            return true;
        }
        return this.selectedMethod() != null;
    });

    constructor() {
        const labelFn = (o: ICompanyLogin) => o?.name ?? ""
        this.forgotOrgComboboxOptions = {
            data: this.organizationsArr,
            selected: this.selectedOrg,
            enable: ko.pureComputed(() => this.isReady()),
            emptyPreparedListMessage: NO_DATA_MESSAGE,
            emptyFilterMessage: "Ingen organisasjoner matcher søket",
            wrapperClass: "login-page__org-combobox",
            inputClass: "login-page__org-native",
            inputAttrs: {
                placeholder: ko.pureComputed(() => lm.t("choose organization|td") ?? ''),
                title: ko.pureComputed(() => lm.t("((Choose organization))") ?? ''),
                ariaLabel: ko.pureComputed(() => lm.t("((Choose organization))") ?? ''),
                autocomplete: "off",
            },
            option: {
                label: labelFn,
                html: labelFn,
                match: createBaseTextMatch(labelFn, ["name", "orgnr"]),
            },
            prepareData: {
                sort: (items) => _.sortBy(items, (o: ICompanyLogin) => (o?.name ?? "").toLowerCase()),
            },
        };

        this.kosub.subscribe(this.selectedOrg, () => {
            this.selectedMethod(null);
            this.methodFormVisible(false);
        });

        this.init();
    }

    continueForgotStep = (): void => {
        if (!this.canContinueForgot()) {
            return;
        }
        const methods = this.availableMethods();
        if (methods.length === 1) {
            this.selectedMethod(methods[0] ?? null);
        } else if (!this.selectedMethod()) {
            return;
        }
        this.methodFormVisible(true);
    };

    private init() {
        this.messageData({message: REQUEST_MESSAGE});
        this.reqGetCompanyLogin
            .request()
            .then((data: ICompanyLogin[]) => {
                this.organizations = _.sortBy(_.cloneDeep(data), (cl: ICompanyLogin) => cl.name?.toLowerCase());
                this.organizationsArr(this.organizations);
                const orgnr = global.router.currentRoute()?.__routeParams?.['orgnr']
                    ?? global.router.currentRoute()?.__routeParams?.['orgid'];
                // Empty by default — user must pick their org (avoids continuing as Identum AS by mistake).
                // Only pre-select when orgnr/orgid is in the URL (e.g. deep link from login).
                const next = orgnr
                    ? (this.organizations.find(o => o.orgnr == orgnr) ?? null)
                    : null;
                this.selectedOrg(next);
                this.messageData(null);
                this.isReady(true);
                this.tryAutoAdvanceFromLogin(orgnr);
            })
            .catch((reason: unknown) => this.messageData(createErrorMessage(String(reason ?? ""))));
    }

    /**
     * Fra innlogging (orgnr i URL): hopp over org/metode-steg når kun én reset-metode finnes.
     * Flere metoder (f.eks. SMS + ID-porten) beholder valgskjermen.
     */
    private tryAutoAdvanceFromLogin(orgnr: string | undefined): void {
        if (!orgnr || !this.selectedOrg()) {
            return;
        }
        const methods = this.availableMethods();
        if (methods.length !== 1) {
            return;
        }
        this.selectedMethod(methods[0] ?? null);
        this.methodFormVisible(true);
    }

    dispose() {
        this.kosub.unsuscribeAll();
    }
}
